<script lang="ts">
  import { tStore } from '../../../i18n'
  import Tooltip from '../../../components/Tooltip.svelte'
  import Dropdown from '../../../components/Dropdown.svelte'

  // The bulk-AI-analysis toolbar (left side) is dashboard-specific — every
  // prop for it is optional, and the toolbar only renders when the caller
  // opts in by passing onToggleBulkMode. Other pages (e.g. AI History) reuse
  // this component for page-size/prev/next/jump navigation alone.
  let {
    pageSize,
    offset,
    rangeFrom,
    rangeTo,
    currentPage,
    totalPages,
    totalCount,
    hasMore,
    loading,
    pageSizes,
    bulkMode = false,
    selectedCount = 0,
    bulkSelectionLimit = 0,
    occupiedBulkSlots = 0,
    hasBulkResults = false,
    onPageSizeChange,
    onPrev,
    onNext,
    onGoToPage,
    onToggleBulkMode,
    onRunBulkAi,
    onViewBulkResults,
  }: {
    pageSize: number
    offset: number
    rangeFrom: number
    rangeTo: number
    currentPage: number
    totalPages: number | null
    totalCount: number | null
    hasMore: boolean
    loading: boolean
    pageSizes: readonly number[]
    bulkMode?: boolean
    selectedCount?: number
    bulkSelectionLimit?: number
    occupiedBulkSlots?: number
    hasBulkResults?: boolean
    onPageSizeChange: (size: number) => void
    onPrev: () => void
    onNext: () => void
    onGoToPage: (page: number) => void
    onToggleBulkMode?: () => void
    onRunBulkAi?: () => void
    onViewBulkResults?: () => void
  } = $props()

  // Returns a windowed list of page numbers with null as an ellipsis marker.
  // Always shows first + last page; shows up to 3 pages around the current one.
  function getPageNumbers(current: number, total: number): (number | null)[] {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1)
    }
    const result: (number | null)[] = [1]
    const rangeStart = Math.max(2, current - 1)
    const rangeEnd = Math.min(total - 1, current + 1)
    if (rangeStart > 2) result.push(null)
    for (let i = rangeStart; i <= rangeEnd; i++) result.push(i)
    if (rangeEnd < total - 1) result.push(null)
    result.push(total)
    return result
  }

  // Jump-to-page state: tracks which ellipsis slot is open (by list index).
  let jumpingAt = $state<number | null>(null)
  let jumpValue = $state('')

  function openJump(index: number) {
    jumpingAt = index
    jumpValue = ''
  }

  function commitJump() {
    const page = parseInt(jumpValue, 10)
    if (totalPages !== null && page >= 1 && page <= totalPages) {
      onGoToPage(page)
    }
    jumpingAt = null
    jumpValue = ''
  }

  function cancelJump() {
    jumpingAt = null
    jumpValue = ''
  }

  const pageSizeOptions = $derived(pageSizes.map((size) => ({ value: size, label: String(size) })))

  function onJumpKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') commitJump()
    else if (e.key === 'Escape') cancelJump()
  }
</script>

<footer class="pager">
  <div class="pager__bulk">
    <label class="pager__size">
      <span class="field__label">{$tStore('views.pagination.pageSize')}</span>
      <Dropdown value={pageSize} options={pageSizeOptions} onchange={onPageSizeChange} />
    </label>
    {#if onToggleBulkMode}
      <Tooltip
        text={bulkMode
          ? $tStore('views.dashboard.exitBulkAiAnalysisTooltip')
          : $tStore('views.dashboard.bulkAiAnalysisTooltip', { max: bulkSelectionLimit })}
      >
        <button
          type="button"
          class="btn btn--secondary btn--sm bulk-toggle"
          class:btn--active-danger={bulkMode}
          onclick={onToggleBulkMode}
          aria-pressed={bulkMode}
        >
          {$tStore(
            bulkMode ? 'views.dashboard.exitBulkAiAnalysis' : 'views.dashboard.bulkAiAnalysis',
          )}
        </button>
      </Tooltip>
      {#if bulkMode}
        <button
          type="button"
          class="btn btn--primary btn--sm run-bulk-ai"
          onclick={onRunBulkAi}
          disabled={selectedCount === 0}
          title={$tStore('views.dashboard.runBulkAiTooltip')}
        >
          {$tStore('views.dashboard.runBulkAi')}{selectedCount > 0 ? ` (${selectedCount})` : ''}
        </button>
        {#if hasBulkResults}
          <button type="button" class="bulk-slots bulk-slots--link" onclick={onViewBulkResults}>
            {$tStore('views.dashboard.bulkSlotsInUse', {
              occupied: occupiedBulkSlots,
              max: bulkSelectionLimit,
            })}
          </button>
        {:else}
          <span class="bulk-slots">
            {$tStore('views.dashboard.bulkSlotsInUse', {
              occupied: occupiedBulkSlots,
              max: bulkSelectionLimit,
            })}
          </span>
        {/if}
      {/if}
    {/if}
  </div>

  <div class="pager__info">
    <span>
      {$tStore('views.pagination.showing')}
      {rangeFrom}–{rangeTo}{totalCount !== null
        ? ` ${$tStore('views.pagination.of')} ${totalCount}`
        : ''}
    </span>
  </div>

  <div class="pager__nav">
    <button
      type="button"
      class="btn btn--secondary btn--sm"
      onclick={onPrev}
      disabled={offset === 0 || loading}
    >
      ‹ {$tStore('views.pagination.previous')}
    </button>

    {#if totalPages !== null && totalPages > 0}
      {#each getPageNumbers(currentPage, totalPages) as item, i (i)}
        {#if item === null}
          {#if jumpingAt === i}
            <input
              class="pager__jump-input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              placeholder="…"
              bind:value={jumpValue}
              onkeydown={onJumpKeydown}
              onblur={commitJump}
              autofocus
            />
          {:else}
            <button
              type="button"
              class="pager__ellipsis"
              title="Jump to page"
              onclick={() => openJump(i)}>…</button
            >
          {/if}
        {:else}
          <button
            type="button"
            class="btn btn--sm pager__page"
            class:btn--primary={item === currentPage}
            class:btn--secondary={item !== currentPage}
            disabled={loading || item === currentPage}
            aria-current={item === currentPage ? 'page' : undefined}
            onclick={() => onGoToPage(item as number)}
          >
            {item}
          </button>
        {/if}
      {/each}
    {/if}

    <button
      type="button"
      class="btn btn--secondary btn--sm"
      onclick={onNext}
      disabled={!hasMore || loading}
    >
      {$tStore('views.pagination.next')} ›
    </button>
  </div>
</footer>

<style lang="scss">
  @use '../../../assets/styles/variables' as v;

  .pager {
    display: flex;
    align-items: center;
    gap: v.$space-md;
    flex-wrap: wrap;
    padding-top: v.$space-sm;
    border-top: 1px solid v.$color-border;

    // The page-size menu opens downward. While it is open, reserve room for it
    // so it stays inside the layout instead of extending the page past the
    // sidebar; at rest the pager adds no extra space.
    &:has(:global(.dropdown__menu)) {
      padding-bottom: 10rem;
    }
  }

  .pager__bulk {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    flex-wrap: wrap;
  }

  // Live "N/max slots" readout — muted so it reads as status text, not an
  // action, distinguishing it from the buttons it sits next to.
  .bulk-slots {
    font-size: 0.8125rem;
    color: v.$color-text-muted;
    white-space: nowrap;
  }

  // Clickable variant — reopens the results panel without losing the
  // accumulated record, so closing it isn't a dead end while tenders from an
  // earlier batch are still running.
  .bulk-slots--link {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: v.$color-text;
    }
  }

  .pager__size {
    display: flex;
    align-items: center;
    gap: v.$space-sm;

    .field__label {
      margin: 0;
    }

    // Dropdown renders its trigger inside its own component, so scoped
    // styles can't reach it without :global().
    :global(.dropdown),
    :global(.dropdown__trigger) {
      width: auto;
    }
  }

  .pager__info {
    display: flex;
    align-items: center;
    gap: v.$space-sm;
    color: v.$color-text-muted;
    font-size: 0.875rem;
    flex: 1;
    justify-content: center;
  }

  .pager__nav {
    display: flex;
    align-items: center;
    gap: v.$space-xs;
    margin-left: auto;
    flex-wrap: wrap;
  }

  .pager__ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    font-size: 0.8125rem;
    color: v.$color-text-muted;
    background: none;
    border: 1px solid transparent;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    padding: 0;
    letter-spacing: 0.05em;

    &:hover {
      border-color: v.$color-border;
      color: v.$color-text;
    }
  }

  .pager__jump-input {
    width: 3.5rem;
    height: 2rem;
    padding: 0 v.$space-xs;
    font-size: 0.8125rem;
    text-align: center;
    border: 1px solid v.$color-border;
    border-radius: 4px;
    background: v.$color-bg;
    color: v.$color-text;

    &:focus {
      outline: none;
      border-color: v.$color-primary;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
    -moz-appearance: textfield;
  }

  // Current page indicator: keep full opacity so it reads as active, not
  // disabled. The btn--primary colour already communicates "you are here".
  .pager__page[aria-current='page']:disabled {
    opacity: 1;
    cursor: default;
  }

  .pager__page {
    min-width: 2rem;
    padding-left: v.$space-xs;
    padding-right: v.$space-xs;
  }
</style>
